type FindingType = 'email' | 'phone' | 'card' | 'ssn' | 'key';

interface Finding {
  type: FindingType;
  start: number;
  end: number;
  value: string;
}

const TYPES: Record<FindingType, { name: string; one: string; many: string; tier: 1 | 2 | 3 }> = {
  email: { name: 'Email address', one: 'email address', many: 'email addresses', tier: 1 },
  phone: { name: 'Phone number', one: 'phone number', many: 'phone numbers', tier: 1 },
  card: { name: 'Card number', one: 'card number', many: 'card numbers', tier: 2 },
  ssn: {
    name: 'Social security number',
    one: 'social security number',
    many: 'social security numbers',
    tier: 3,
  },
  key: { name: 'API key or secret', one: 'API key', many: 'API keys', tier: 3 },
};

const TIER_NAME = { 1: 'Low', 2: 'Medium', 3: 'High' };
const RESULT = { 1: 'Warning', 2: 'Masked', 3: 'Blocked' };

const PRESETS: Record<string, string> = {
  mixed: [
    'Please draft a polite refund email to maria.lopez@example.com.',
    'Her card 4242 4242 4242 4242 was charged twice.',
    'If she calls, her number is +1 415 555 0132.',
  ].join(' '),
  key: [
    'Why does this call fail? I am sending the header',
    'Authorization: Bearer sk-demo-48fj29dk3l0aq7zm51xv to the billing service.',
  ].join(' '),
  clean: [
    'Summarise the main differences between a semantic cache',
    'and an exact-match cache in three short bullet points.',
  ].join(' '),
};

const esc = (value: string) =>
  value.replace(/[&<>"']/g, (char) => {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return map[char];
  });

const luhn = (digits: string) => {
  const sum = digits
    .split('')
    .reverse()
    .reduce((total, char, index) => {
      let digit = Number(char);
      if (index % 2 === 1) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      return total + digit;
    }, 0);
  return sum % 10 === 0;
};

const detect = (text: string): Finding[] => {
  const found: Finding[] = [];
  const taken: Array<[number, number]> = [];
  const free = (start: number, end: number) =>
    !taken.some(([from, to]) => start < to && end > from);
  const add = (match: RegExpMatchArray, type: FindingType) => {
    const start = match.index ?? 0;
    const end = start + match[0].length;
    if (!free(start, end)) return;
    taken.push([start, end]);
    found.push({ type, start, end, value: match[0] });
  };

  const keyPattern = /\b(?:sk|pk|rk)[-_][A-Za-z0-9][A-Za-z0-9_-]{15,}\b/g;
  Array.from(text.matchAll(keyPattern)).forEach((match) => add(match, 'key'));
  Array.from(text.matchAll(/\b\d{3}-\d{2}-\d{4}\b/g)).forEach((match) => add(match, 'ssn'));
  Array.from(text.matchAll(/\b(?:\d[ -]?){12,18}\d\b/g)).forEach((match) => {
    const digits = match[0].replace(/\D/g, '');
    if (digits.length >= 13 && digits.length <= 19 && luhn(digits)) add(match, 'card');
  });
  Array.from(text.matchAll(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g)).forEach((match) =>
    add(match, 'email'),
  );
  Array.from(text.matchAll(/(?<!\w)\+?\d[\d\s().-]{8,16}\d(?!\w)/g)).forEach((match) => {
    const digits = match[0].replace(/\D/g, '');
    const start = match.index ?? 0;
    const before = text.slice(0, start);
    const after = text.slice(start + match[0].length);
    const isFragment = /\d[ -]$/.test(before) || /^[ -]\d/.test(after);
    if (!isFragment && digits.length >= 10 && digits.length <= 13) add(match, 'phone');
  });

  return found.sort((left, right) => left.start - right.start);
};

const preview = (finding: Finding) => {
  const { value } = finding;
  const digits = value.replace(/\D/g, '');
  if (finding.type === 'email') {
    const [user, domain] = value.split('@');
    return `${user[0]}•••@${domain}`;
  }
  if (finding.type === 'phone') return `••• ••• ${digits.slice(-4)}`;
  if (finding.type === 'card') return `•••• ${digits.slice(-4)}`;
  if (finding.type === 'ssn') return `•••-••-${digits.slice(-4)}`;
  return `${value.slice(0, 3)}••••`;
};

const joinParts = (parts: string[]) => {
  if (parts.length < 2) return parts[0] || '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
};

export default function attachLandingBehavior(
  root: HTMLElement,
  logos: { light: string; dark: string },
): () => void {
  const ac = new AbortController();
  const { signal } = ac;
  const q = <T extends Element>(selector: string) => root.querySelector(selector) as T | null;
  let announceTimer = 0;
  let lastSummary: string | null = null;

  const themeQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const themeButton = q<HTMLButtonElement>('#theme');
  const brandLogos = root.querySelectorAll<HTMLImageElement>('.brand img');
  const isDark = () => (root.dataset.theme ? root.dataset.theme === 'dark' : themeQuery.matches);
  const syncTheme = () => {
    const dark = isDark();
    themeButton?.setAttribute('aria-pressed', String(dark));
    brandLogos.forEach((img) => {
      const logo = img;
      logo.src = dark ? logos.dark : logos.light;
    });
  };
  themeButton?.addEventListener(
    'click',
    () => {
      const page = root;
      page.dataset.theme = isDark() ? 'light' : 'dark';
      syncTheme();
    },
    { signal },
  );
  themeQuery.addEventListener('change', syncTheme, { signal });
  syncTheme();

  const live = q<HTMLElement>('#live');
  const announce = (message: string) => {
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => {
      live.textContent = message;
    }, 60);
  };

  const header = q<HTMLElement>('#header');
  const onScroll = () => header?.classList.toggle('scrolled', window.scrollY > 8);
  window.addEventListener('scroll', onScroll, { passive: true, signal });
  onScroll();

  const menu = q<HTMLButtonElement>('#menu');
  const nav = q<HTMLElement>('#nav');
  const closeMenu = () => {
    nav?.classList.remove('open');
    menu?.setAttribute('aria-expanded', 'false');
  };
  menu?.addEventListener(
    'click',
    () => {
      const open = nav?.classList.toggle('open');
      menu?.setAttribute('aria-expanded', String(Boolean(open)));
    },
    { signal },
  );
  nav?.addEventListener(
    'click',
    (event) => {
      if ((event.target as Element | null)?.closest('a')) closeMenu();
    },
    { signal },
  );
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key === 'Escape' && nav?.classList.contains('open')) {
        closeMenu();
        menu?.focus();
      }
    },
    { signal },
  );

  const textarea = q<HTMLTextAreaElement>('#demo-input');
  const highlight = q<HTMLElement>('#demo-hl');
  const banner = q<HTMLElement>('#demo-banner');
  const list = q<HTMLElement>('#demo-findings');
  const seen = q<HTMLElement>('#demo-seen');
  const restore = q<HTMLElement>('#demo-restore');
  const send = q<HTMLButtonElement>('#demo-send');
  const demoMsg = q<HTMLElement>('#demo-msg');

  const render = () => {
    if (!textarea || !highlight || !banner || !list || !seen || !restore || !send) return;
    const text = textarea.value;
    const findings = detect(text);
    let html = '';
    let cursor = 0;
    findings.forEach((finding) => {
      html += esc(text.slice(cursor, finding.start));
      html += `<mark class="t${TYPES[finding.type].tier}">${esc(finding.value)}</mark>`;
      cursor = finding.end;
    });
    html += esc(text.slice(cursor));
    highlight.innerHTML = `${html}\u200b`;
    highlight.scrollTop = textarea.scrollTop;

    const hasTier = (tier: 1 | 2 | 3) =>
      findings.some((finding) => TYPES[finding.type].tier === tier);
    const counts: Partial<Record<FindingType, number>> = {};
    findings.forEach((finding) => {
      counts[finding.type] = (counts[finding.type] || 0) + 1;
    });
    const parts = (Object.entries(counts) as Array<[FindingType, number]>).map(([type, count]) => {
      const label = count === 1 ? TYPES[type].one : TYPES[type].many;
      return `${count} ${label}`;
    });
    const joined = joinParts(parts);

    let className = 'banner';
    let bannerHtml = 'Start typing to check a message.';
    if (!text.trim()) {
      bannerHtml = 'Start typing to check a message.';
    } else if (hasTier(3)) {
      className += ' banner--block';
      bannerHtml = [
        "<strong>Can't send.</strong> Personal data detected in your message:",
        `${joined}. Level 3 values are blocked by default. Remove them to send.`,
      ].join(' ');
    } else if (hasTier(2)) {
      className += ' banner--mask';
      const warningNote = hasTier(1) ? ' Other values are a warning only and go as typed.' : '';
      bannerHtml = [
        '<strong>Will be masked before sending.</strong>',
        `Personal data detected in your message: ${joined}.${warningNote}`,
      ].join(' ');
    } else if (hasTier(1)) {
      className += ' banner--warn';
      bannerHtml = [
        '<strong>Warning only.</strong> Personal data detected in your message:',
        `${joined}. It is sent as typed.`,
      ].join(' ');
    } else {
      className += ' banner--ok';
      bannerHtml = '<strong>Nothing sensitive found.</strong> The message is sent as typed.';
    }
    banner.className = className;
    banner.innerHTML = bannerHtml;

    list.innerHTML = findings
      .map((finding) => {
        const { tier, name } = TYPES[finding.type];
        const tierLabel = TIER_NAME[tier];
        const result = RESULT[tier];
        const sample = esc(preview(finding));
        return [
          '<li><div class="f-top">',
          `<span class="tier t${tier}">Level ${tier} <span>${tierLabel}</span></span>`,
          `<span class="f-res">${result}</span></div>`,
          `<div class="f-type">${name}<code>${sample}</code></div></li>`,
        ].join('');
      })
      .join('');

    if (!text.trim()) {
      seen.textContent = '';
    } else if (hasTier(3)) {
      seen.textContent = 'Nothing. The message is not sent until the highlighted value is removed.';
    } else {
      const counters: Partial<Record<FindingType, number>> = {};
      let out = '';
      let index = 0;
      findings.forEach((finding) => {
        out += esc(text.slice(index, finding.start));
        const { tier } = TYPES[finding.type];
        if (tier === 2) {
          counters[finding.type] = (counters[finding.type] || 0) + 1;
          const token = `${finding.type.toUpperCase()}_${counters[finding.type]}`;
          out += `<span class="token">[${token}]</span>`;
        } else {
          out += `<span class="t1">${esc(finding.value)}</span>`;
        }
        index = finding.end;
      });
      out += esc(text.slice(index));
      seen.innerHTML = out;
    }
    restore.hidden = !(hasTier(2) && !hasTier(3));
    send.setAttribute('aria-disabled', String(!text.trim() || hasTier(3)));

    const summary = banner.textContent || '';
    window.clearTimeout(announceTimer);
    announceTimer = window.setTimeout(() => {
      if (lastSummary !== null && summary !== lastSummary) announce(summary);
      lastSummary = summary;
    }, 600);
  };

  if (textarea && highlight && send && demoMsg) {
    textarea.addEventListener(
      'input',
      () => {
        demoMsg.textContent = '';
        render();
      },
      { signal },
    );
    textarea.addEventListener(
      'scroll',
      () => {
        highlight.scrollTop = textarea.scrollTop;
      },
      { signal },
    );
    root.querySelectorAll<HTMLButtonElement>('[data-preset]').forEach((button) => {
      button.addEventListener(
        'click',
        () => {
          const preset = button.dataset.preset || '';
          textarea.value = PRESETS[preset] || '';
          demoMsg.textContent = '';
          render();
          textarea.focus({ preventScroll: true });
        },
        { signal },
      );
    });
    send.addEventListener(
      'click',
      () => {
        const text = textarea.value;
        if (!text.trim()) {
          demoMsg.textContent = 'Type a message first.';
          return;
        }
        if (detect(text).some((finding) => TYPES[finding.type].tier === 3)) {
          demoMsg.textContent = "Can't send. Remove the highlighted Level 3 value first.";
          return;
        }
        demoMsg.textContent = 'Preview only. Nothing was sent.';
      },
      { signal },
    );
    textarea.value = PRESETS.mixed;
    render();
    lastSummary = banner?.textContent || '';
  }

  let observer: IntersectionObserver | undefined;
  if (
    'IntersectionObserver' in window &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('in');
          observer?.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px' },
    );
    const revealTargets =
      '.center-head, .feat-grid, .cost-grid, .story-grid, .flow-steps, .window-wrap, .specs-card, .cta';
    root.querySelectorAll(revealTargets).forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight * 0.92) return;
      el.classList.add('reveal');
      observer?.observe(el);
    });
  }

  return () => {
    ac.abort();
    observer?.disconnect();
    window.clearTimeout(announceTimer);
  };
}
